import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { providerKeyOf } from './teamCreditsUsage'
import { formatDateTime, formatNumber } from '../../../i18n/dateFormat'
import { useUiLocale } from '../../../i18n/LocaleProvider'

/** Shared tiny formatters for the Team pages. Decimal strings come from the API. */
export function useTeamFormat() {
  const { locale } = useUiLocale()
  const num = useCallback(
    (value: string | number | null | undefined): string => {
      if (value === null || value === undefined || value === '') return '—'
      const n = typeof value === 'number' ? value : Number(value)
      if (!Number.isFinite(n)) return '—'
      return formatNumber(n, locale, { maximumFractionDigits: 2 })
    },
    [locale],
  )
  const dateTime = useCallback(
    (value: string | null | undefined): string => {
      if (!value) return '—'
      // The API returns naive-UTC ISO strings; mark them UTC so the browser localises.
      const iso = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`
      try {
        return formatDateTime(iso, locale)
      } catch {
        return '—'
      }
    },
    [locale],
  )
  return { num, dateTime, locale }
}

/** Avatar: uploaded team image, otherwise the first letter on a neutral tile. */
export function TeamAvatar({ src, name, size = 56, round = false }: { src?: string | null; name: string; size?: number; round?: boolean }) {
  const style = { width: size, height: size, borderRadius: round ? '50%' : Math.round(size * 0.22), fontSize: Math.round(size * 0.42) }
  if (src) {
    return <img className="tm-avatar" src={src} alt={name} style={style} />
  }
  return (
    <span className="tm-avatar tm-avatar-fallback" style={style} aria-hidden="true">
      {(name || '?').trim().charAt(0).toUpperCase() || '?'}
    </span>
  )
}

/**
 * Product display name for a ledger `provider_key`. Known keys are i18n entries;
 * an unknown key is shown as-is (never hidden, never guessed), and a missing
 * provider is 未知. Internal artifact / worker / routing metadata is never part of
 * the payload, so there is nothing else to leak here.
 */
export function useProviderLabel() {
  const { t } = useTranslation('team')
  return useCallback(
    (providerKey: string | null): string => {
      const key = providerKeyOf(providerKey)
      return t(`provider.${key}`, { defaultValue: key === 'unknown' ? t('provider.unknown') : (providerKey ?? '').trim() })
    },
    [t],
  )
}
